import { addressLine } from "./locationAddress";
import { LocationPros } from "../types/baseTypes";

/**
 * The embedded Google map for one address — one place for every static map
 * the app shows (the Standorte tab, and the static fallback on the venue and
 * room pages).
 *
 * The coordinates come straight off the API and are often absent: a house or
 * a station created through the wizard never had any (they were asked for only
 * when a room was created at a new address). Interpolated as they were, an
 * absent pair became `q=null,null`, which Google geocodes as text — the map
 * showed a road in France and its "Maps" button searched for "null,null".
 *
 * So: the coordinates when both are real numbers, otherwise the address as
 * text (Google finds "Weyertal 1, 50931 Köln" reliably), otherwise no map at
 * all — a map of nowhere is worse than none.
 */

type MaybeAddress = Partial<LocationPros["address"]> | null | undefined;

/** A finite coordinate, or null — `Number(null)` is 0, so null is rejected first. */
export const toCoordinate = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/** What the map is centred on: a coordinate pair, the address text, or nothing. */
export const mapQueryOf = (
    address: MaybeAddress,
    coordinates?: { lat: unknown; lng: unknown },
): string | null => {
    const lat = toCoordinate(coordinates ? coordinates.lat : address?.geoLat);
    const lng = toCoordinate(coordinates ? coordinates.lng : address?.geoLng);
    if (lat !== null && lng !== null) return `${lat},${lng}`;
    const line = addressLine(address);
    return line.length > 0 ? line : null;
};

/** The iframe `src`, or null when there is nothing to show a map of. */
export const mapEmbedSrc = (
    address: MaybeAddress,
    coordinates?: { lat: unknown; lng: unknown },
): string | null => {
    const query = mapQueryOf(address, coordinates);
    return query === null
        ? null
        : `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
};
